import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import AppButton from '@/components/ui/AppButton';
import { toast } from '../lib/swal';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/lib/utils';
import referralService from '../services/referralService';
import { CheckCircle, DollarSign, Banknote, CreditCard, ShieldCheck, ArrowLeft, Copy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FastCheckoutModal = ({ 
  isOpen, 
  onClose, 
  sorteo, 
  paqueteId, 
  paqueteData, 
  cantidad = 1, 
  numerosBoletos = []
}) => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  
  const [metodoPago, setMetodoPago] = useState('transferencia');
  const [comprando, setComprando] = useState(false);
  const [numeroComprobante, setNumeroComprobante] = useState('');
  
  // Fast Checkout states
  const [fastCheckoutStep, setFastCheckoutStep] = useState(1);
  const [fastCheckoutEmail, setFastCheckoutEmail] = useState('');
  const [fastCheckoutName, setFastCheckoutName] = useState('');
  const [fastCheckoutCedula, setFastCheckoutCedula] = useState('');
  const [fastCheckoutCelular, setFastCheckoutCelular] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [formError, setFormError] = useState('');
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  const formStartedFired = useRef(false);

  // Settings
  const [configuracionAdmin, setConfiguracionAdmin] = useState(null);
  const [payphoneSettings, setPayphoneSettings] = useState(null);
  const [paypalSettings, setPaypalSettings] = useState(null);

  // Pasarelas states
  const [showPayphoneButton, setShowPayphoneButton] = useState(false);
  const [clientTxId, setClientTxId] = useState(null);
  const [showPaypalButtons, setShowPaypalButtons] = useState(false);
  const [paypalPurchaseData, setPaypalPurchaseData] = useState(null);

  const vendedorLink = new URLSearchParams(window.location.search).get('ref');

  // Calcular valor estimado para tracking
  const calcularValorEstimado = () => {
    const precioBase = Number(sorteo?.precio_boleto) || 0;
    if (paqueteData) {
      const cant = Number(paqueteData.cantidad) || 0;
      const desc = (Number(paqueteData.descuento) || 0) / 100;
      return Number((cant * precioBase * (1 - desc)).toFixed(2));
    }
    return Number((precioBase * (Number(cantidad) || 1)).toFixed(2));
  };

  useEffect(() => {
    if (isOpen) {
      if (window.fbq) {
        const estimatedValue = calcularValorEstimado();
        window.fbq('track', 'InitiateCheckout', {
          currency: 'USD',
          value: estimatedValue,
          content_name: paqueteData ? `Paquete ${paqueteData.cantidad} boletos` : (sorteo?.titulo || 'Boletos'),
          content_type: 'product',
          num_items: paqueteData ? (paqueteData.cantidad + (paqueteData.boletos_gratis || 0)) : (cantidad || 1)
        });
        window.fbq('track', 'BeginCheckout', {
          currency: 'USD',
          value: estimatedValue
        });
      }
      formStartedFired.current = false;
      fetchSettings();
      // Initialize state based on user
      if (user) {
        setFastCheckoutStep(3);
      } else {
        setFastCheckoutStep(1);
        setFastCheckoutEmail('');
        setFastCheckoutName('');
        setFastCheckoutCedula('');
        setFastCheckoutCelular('');
      }
      setShowPayphoneButton(false);
      setShowPaypalButtons(false);
      setMetodoPago('transferencia');
      setNumeroComprobante('');
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (fastCheckoutEmail.length > 0 && !formStartedFired.current) {
      if (window.fbq) window.fbq('trackCustom', 'FormStarted');
      formStartedFired.current = true;
    }
  }, [fastCheckoutEmail]);

  useEffect(() => {
    if (isOpen && fastCheckoutStep === 3 && window.fbq) {
      const estimatedValue = calcularValorEstimado();
      window.fbq('track', 'AddPaymentInfo', { 
        currency: 'USD',
        value: estimatedValue,
        payment_type: metodoPago 
      });
    }
  }, [metodoPago, isOpen, fastCheckoutStep]);

  // Smart Email Checking Effect
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (fastCheckoutStep === 1 && emailRegex.test(fastCheckoutEmail)) {
      const timer = setTimeout(async () => {
        setCheckingEmail(true);
        try {
          const response = await axios.post(`${API}/auth/check-email`, { email: fastCheckoutEmail });
          if (response.data.exists) {
            setIsNewUser(false);
          } else {
            setIsNewUser(true);
          }
        } catch (error) {
          console.error('Error checking email:', error);
        } finally {
          setCheckingEmail(false);
        }
      }, 500);
      return () => clearTimeout(timer);
    } else if (fastCheckoutStep === 1) {
      setIsNewUser(false);
    }
  }, [fastCheckoutEmail, fastCheckoutStep]);

  const fetchSettings = async () => {
    try {
      const [conf, payphone, paypal] = await Promise.all([
        axios.get(`${API}/configuracion-publica`).catch(() => ({ data: null })),
        axios.get(`${API}/public/payphone-settings`).catch(() => ({ data: null })),
        axios.get(`${API}/public/paypal-settings`).catch(() => ({ data: null }))
      ]);
      if (conf.data) setConfiguracionAdmin(conf.data);
      if (payphone.data) setPayphoneSettings(payphone.data);
      if (paypal.data) setPaypalSettings(paypal.data);
    } catch (error) {
      console.error('Error al cargar configuraciones:', error);
    }
  };

  const copyDatosBancarios = () => {
    if (!configuracionAdmin) return;
    const texto = `BANCO: ${configuracionAdmin.banco}\nCUENTA: ${configuracionAdmin.numero_cuenta}\nTIPO: ${configuracionAdmin.tipo_cuenta}\nTITULAR: ${configuracionAdmin.nombre_titular}\nCÉDULA/RUC: ${configuracionAdmin.cedula_ruc}\nCORREO: ${configuracionAdmin.correo_pagos}`;
    navigator.clipboard.writeText(texto);
    toast.success('Datos bancarios copiados');
  };

  const handleCheckEmail = async () => {
    if (!fastCheckoutEmail || !fastCheckoutEmail.includes('@')) {
      toast.error('Ingresa un correo electrónico válido');
      return;
    }
    if (isNewUser) {
      if (!fastCheckoutName || !fastCheckoutCedula || !fastCheckoutCelular) {
        setFormError('Por favor, completa todos tus datos para continuar.');
        return;
      }
    }
    setFormError('');
    if (window.fbq) window.fbq('trackCustom', 'FormCompleted');
    setFastCheckoutStep(3);
  };

  const getBasePayload = (metodo) => {
    let payload = {
      sorteo_id: sorteo?.id,
      metodo_pago: metodo,
      vendedor_id: referralService.getVendedorForPurchase(),
      vendedor_link: vendedorLink
    };
    
    if (!user) {
      payload.fast_checkout_email = fastCheckoutEmail;
      if (fastCheckoutEmail) {
        localStorage.setItem('fastCheckoutEmail', fastCheckoutEmail);
      }
      if (isNewUser) {
        payload.fast_checkout_name = fastCheckoutName;
        payload.fast_checkout_cedula = fastCheckoutCedula;
        payload.fast_checkout_celular = fastCheckoutCelular;
      }
    }

    if (paqueteId) {
      payload.paquete_id = paqueteId;
    } else {
      const numerosValidos = numerosBoletos.filter(n => n && typeof n === 'string' && n.trim() !== '').map(n => parseInt(n));
      payload.numeros_boletos = numerosValidos;
    }
    return payload;
  };

  const handleComprarTransferencia = async () => {
    if (!user && fastCheckoutStep !== 3) {
      toast.error('Debes completar el proceso de correo primero');
      return;
    }

    const payload = getBasePayload('transferencia');
    payload.numero_comprobante = numeroComprobante;

    setComprando(true);
    try {
      const response = await axios.post(`${API}/boletos/comprar`, payload);
      toast.success(response.data.message);

      if (payload.vendedor_id) {
        referralService.clearVendedor();
      }

      if (response.data.session_token) {
        localStorage.setItem('ww_session_token', response.data.session_token);
        await checkAuth();
      }

      onClose();
      toast.info('Redirigiendo a tu panel para ver tus boletos pendientes...');
      setTimeout(() => {
        navigate('/usuario?tab=boletos');
      }, 1500);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('completar tus datos')) {
        toast.error('Debes completar tus datos antes de comprar');
        navigate('/completar-datos');
      } else {
        toast.error(error.response?.data?.detail || 'Error al comprar boletos');
      }
    } finally {
      setComprando(false);
    }
  };

  const handleProceedToPayPhone = async () => {
    if (!user && fastCheckoutStep !== 3) {
      toast.error('Debes completar el proceso de correo primero');
      return;
    }

    setComprando(true);
    try {
      const payload = getBasePayload('payphone');
      const response = await axios.post(`${API}/boletos/comprar`, payload);
      const { payphone_data, session_token } = response.data;

      if (session_token) {
        localStorage.setItem('ww_session_token', session_token);
        await checkAuth();
      }

      if (!payphone_data) {
        toast.error('No se recibió la configuración de PayPhone');
        setComprando(false);
        return;
      }

      sessionStorage.setItem('payphone_pending_tx', payphone_data.clientTransactionId);
      setClientTxId(payphone_data.clientTransactionId);
      setShowPayphoneButton(true);

      const waitForSdk = (retries = 20) => new Promise((resolve, reject) => {
        const check = () => {
          if (typeof window.PPaymentButtonBox !== 'undefined') {
            resolve();
          } else if (retries-- <= 0) {
            reject(new Error('SDK de PayPhone no se cargó'));
          } else {
            setTimeout(check, 250);
          }
        };
        check();
      });

      setTimeout(async () => {
        const container = document.getElementById('pp-button');
        if (!container) {
          toast.error('Contenedor de PayPhone no encontrado');
          setComprando(false);
          return;
        }
        container.innerHTML = '';

        try {
          await waitForSdk();
          new window.PPaymentButtonBox({
            token: payphone_data.token,
            clientTransactionId: payphone_data.clientTransactionId,
            amount: payphone_data.amount,
            amountWithoutTax: payphone_data.amountWithoutTax,
            amountWithTax: payphone_data.amountWithTax || 0,
            tax: payphone_data.tax || 0,
            service: payphone_data.service || 0,
            tip: payphone_data.tip || 0,
            currency: payphone_data.currency || 'USD',
            storeId: payphone_data.storeId,
            reference: payphone_data.reference,
            lang: payphone_data.lang || 'es',
            defaultMethod: payphone_data.defaultMethod || 'card',
            timeZone: payphone_data.timeZone ?? -5
          }).render('pp-button');
        } catch (err) {
          console.error('Error renderizando PayPhone:', err);
          toast.error('Error al cargar la pasarela de pagos: ' + err.message);
          setComprando(false);
        }
      }, 300);

    } catch (error) {
      console.error('Error al iniciar orden PayPhone:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 400 && typeof detail === 'string' && detail.includes('completar tus datos')) {
        toast.error('Debes completar tus datos (cédula y celular) antes de comprar');
        navigate('/completar-datos');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Error al procesar orden de pago');
      }
      setComprando(false);
    }
  };

  const handleProceedToPayPal = async () => {
    if (!user && fastCheckoutStep !== 3) {
      toast.error('Debes completar el proceso de correo primero');
      return;
    }

    setComprando(true);
    try {
      const payload = getBasePayload('paypal');
      const response = await axios.post(`${API}/boletos/comprar`, payload);
      const { paypal_data, session_token } = response.data;

      if (session_token) {
        localStorage.setItem('ww_session_token', session_token);
        await checkAuth();
      }

      setPaypalPurchaseData(paypal_data);
      setShowPaypalButtons(true);
    } catch (error) {
      console.error('Error al iniciar orden PayPal:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('completar tus datos')) {
        toast.error('Debes completar tus datos antes de comprar');
        navigate('/completar-datos');
      } else {
        toast.error(error.response?.data?.detail || 'Error al procesar orden PayPal');
      }
      setComprando(false);
    }
  };

  const handlePayPalCreateOrder = async () => {
    try {
      const response = await axios.post(`${API}/paypal/create-order`, {
        purchase_id: paypalPurchaseData.purchase_id,
        total: paypalPurchaseData.total,
        currency: paypalPurchaseData.currency,
        description: paypalPurchaseData.description
      });
      return response.data.order_id;
    } catch (error) {
      console.error('Error creando orden PayPal:', error);
      const detail = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`PayPal: ${detail}`, { duration: 8000 });
      throw error;
    }
  };

  const handlePayPalApprove = async (data) => {
    try {
      const response = await axios.post(`${API}/paypal/capture-order/${data.orderID}`);
      if (response.data.status === 'success') {
        toast.success('¡Pago con PayPal confirmado exitosamente!');
        if (referralService.getVendedorForPurchase()) {
          referralService.clearVendedor();
        }
        onClose();
        toast.info('Redirigiendo...');
        const montoPaypal = response.data.amount || paypalPurchaseData?.amount;
        const graciasUrl = montoPaypal ? `/gracias?value=${Number(montoPaypal).toFixed(2)}&currency=USD` : '/gracias';
        setTimeout(() => navigate(graciasUrl), 1500);
      } else {
        toast.error(response.data.message || 'El pago no pudo ser completado');
      }
    } catch (error) {
      console.error('Error capturando pago PayPal:', error);
      const detail = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`PayPal: ${detail}`, { duration: 8000 });
    } finally {
      setComprando(false);
    }
  };

  if (!sorteo) return null;

  return (
    <Dialog open={isOpen} modal={!showPayphoneButton} onOpenChange={(open) => { 
      if (!open) {
        if (!comprando || showPayphoneButton || showPaypalButtons) {
          onClose();
          setComprando(false);
        }
      } 
    }}>
      <DialogContent
        className={`${showPayphoneButton ? 'max-w-lg md:max-w-xl' : 'max-w-md'} bg-card border-none shadow-2xl rounded-[2.5rem] overflow-y-auto max-h-[95vh] custom-scrollbar p-0 transition-all duration-300 ${isKeyboardOpen ? '!-translate-y-[80%] sm:!-translate-y-[50%]' : ''}`}
        onInteractOutside={(e) => { if (showPayphoneButton) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (showPayphoneButton) e.preventDefault(); }}
      >
        <DialogHeader className="p-8 pb-4 bg-slate-50 dark:bg-white/5">
          <div className="flex items-center gap-3">
            {!user && fastCheckoutStep === 3 && !showPayphoneButton && !showPaypalButtons && (
              <button 
                onClick={() => setFastCheckoutStep(1)} 
                className="p-1 -ml-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors text-muted-foreground hover:text-foreground"
                title="Volver"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
            )}
            <DialogTitle className="text-2xl font-black tracking-tighter uppercase italic">
              Finalizar <span className="text-orange-500">Compra</span>
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Stepper (Ocultar si ya es usuario o si está en pasarelas) */}
        {!user && !showPayphoneButton && !showPaypalButtons && (
          <div className="px-8 mt-4 mb-2">
            <div className="relative flex items-center justify-between w-full">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-slate-100 dark:bg-slate-800 -z-10" />
              <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 bg-orange-500 transition-all duration-500 -z-10" style={{ width: fastCheckoutStep === 3 ? '100%' : '0%' }} />
              
              <div className="flex flex-col items-center gap-2 bg-card px-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black transition-colors duration-500 ${fastCheckoutStep >= 1 ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
                  1
                </div>
                <span className={`text-[9px] font-black uppercase tracking-wider transition-colors duration-500 ${fastCheckoutStep >= 1 ? 'text-orange-500' : 'text-slate-400'}`}>
                  Identificación
                </span>
              </div>

              <div className="flex flex-col items-center gap-2 bg-card px-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black transition-colors duration-500 ${fastCheckoutStep === 3 ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
                  2
                </div>
                <span className={`text-[9px] font-black uppercase tracking-wider transition-colors duration-500 ${fastCheckoutStep === 3 ? 'text-orange-500' : 'text-slate-400'}`}>
                  Pago
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="p-8 pt-0 space-y-6">
          {!user && fastCheckoutStep === 1 ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Ingresa tu correo para continuar con la compra de forma rápida y segura.</p>
              <div className="space-y-2 relative">
                <Label className="text-xs font-bold uppercase tracking-wider">Correo Electrónico</Label>
                <Input 
                  type="email" 
                  value={fastCheckoutEmail}
                  onChange={e => setFastCheckoutEmail(e.target.value)}
                  onFocus={() => setIsKeyboardOpen(true)}
                  onBlur={() => setIsKeyboardOpen(false)}
                  placeholder="tu@correo.com"
                  className="w-full h-12 px-4 rounded-xl border bg-slate-50 dark:bg-slate-900 focus:ring-2 focus:ring-orange-500 pr-10"
                />
                {checkingEmail && (
                  <div className="absolute right-3 top-9 w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                )}
              </div>

              {isNewUser && (
                <div className="space-y-3 animate-in fade-in slide-in-from-top-4 duration-500">
                  <p className="text-sm text-muted-foreground mt-4 border-t pt-4">¡Hola! Vemos que eres nuevo. Completa tus datos para registrar tus boletos.</p>
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Nombre Completo</Label>
                    <Input type="text" value={fastCheckoutName} onChange={e => setFastCheckoutName(e.target.value)} onFocus={() => setIsKeyboardOpen(true)} onBlur={() => setIsKeyboardOpen(false)} className="w-full h-11 px-3 text-sm rounded-lg border bg-slate-50 dark:bg-slate-900" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Cédula</Label>
                    <Input type="text" value={fastCheckoutCedula} onChange={e => setFastCheckoutCedula(e.target.value)} onFocus={() => setIsKeyboardOpen(true)} onBlur={() => setIsKeyboardOpen(false)} className="w-full h-11 px-3 text-sm rounded-lg border bg-slate-50 dark:bg-slate-900" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Celular</Label>
                    <Input type="text" value={fastCheckoutCelular} onChange={e => setFastCheckoutCelular(e.target.value)} onFocus={() => setIsKeyboardOpen(true)} onBlur={() => setIsKeyboardOpen(false)} className="w-full h-11 px-3 text-sm rounded-lg border bg-slate-50 dark:bg-slate-900" />
                  </div>
                </div>
              )}

              <AppButton 
                className={`w-full h-12 rounded-xl mt-2 transition-all ${(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fastCheckoutEmail) && (!isNewUser || (fastCheckoutName && fastCheckoutCedula && fastCheckoutCelular))) ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg' : 'bg-slate-200 text-slate-400 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-500 border-0 cursor-not-allowed'}`} 
                onClick={handleCheckEmail} 
                loading={checkingEmail} 
                disabled={checkingEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fastCheckoutEmail) || (isNewUser && (!fastCheckoutName || !fastCheckoutCedula || !fastCheckoutCelular))}
              >
                {isNewUser ? 'Ir a Pagar' : 'Continuar'}
              </AppButton>
            </div>
          ) : (
            <>
              {/* Selector de Método de Pago */}
              <div className="flex p-1 bg-slate-100 dark:bg-white/5 rounded-2xl gap-1">
                <button
                  onClick={() => { setMetodoPago('transferencia'); setShowPaypalButtons(false); setShowPayphoneButton(false); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl font-bold uppercase text-[9px] tracking-widest transition-all ${metodoPago === 'transferencia' ? 'bg-white dark:bg-slate-800 shadow-sm text-orange-500' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <Banknote className="w-3.5 h-3.5" /> Transferencia
                </button>
                <button
                  onClick={() => {
                    if (payphoneSettings?.activo) { setMetodoPago('tarjeta'); setShowPaypalButtons(false); setShowPayphoneButton(false); }
                    else toast.info('Pagos con tarjeta no disponibles por ahora');
                  }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl font-bold uppercase text-[9px] tracking-widest transition-all ${metodoPago === 'tarjeta' ? 'bg-white dark:bg-slate-800 shadow-sm text-orange-500' : 'text-muted-foreground hover:text-foreground'}`}
                  disabled={!payphoneSettings?.activo}
                >
                  <CreditCard className="w-3.5 h-3.5" /> Tarjeta {!payphoneSettings?.activo && ''}
                </button>
                {paypalSettings?.activo && (
                  <button
                    onClick={() => {
                      if (paypalSettings?.activo) { setMetodoPago('paypal'); setShowPayphoneButton(false); }
                      else toast.info('Pagos con PayPal no disponibles por ahora');
                    }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl font-bold uppercase text-[9px] tracking-widest transition-all ${metodoPago === 'paypal' ? 'bg-white dark:bg-slate-800 shadow-sm text-[#0070ba]' : 'text-muted-foreground hover:text-foreground'}`}
                    disabled={!paypalSettings?.activo}
                  >
                    <DollarSign className="w-3.5 h-3.5" /> PayPal
                  </button>
                )}
              </div>

              {!showPayphoneButton && !showPaypalButtons ? (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  {metodoPago === 'transferencia' ? (
                    <div className="space-y-4">
                      <div className="p-5 bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20 rounded-3xl">
                        {configuracionAdmin ? (
                          <div className="text-xs space-y-2.5">
                            <div className="flex justify-between items-center opacity-70">
                              <span className="font-bold uppercase tracking-wider">Banco</span>
                              <span className="font-black text-foreground">{configuracionAdmin.banco}</span>
                            </div>
                            <div className="flex justify-between items-center opacity-70">
                              <span className="font-bold uppercase tracking-wider">Cuenta</span>
                              <div className="flex items-center gap-2">
                                <span className="font-black text-foreground">{configuracionAdmin.numero_cuenta}</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(configuracionAdmin.numero_cuenta);
                                    toast.success('Número de cuenta copiado');
                                  }}
                                  className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors p-1 hover:bg-blue-500/10 rounded-md"
                                  title="Copiar solo cuenta"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                            <div className="flex justify-between items-center opacity-70">
                              <span className="font-bold uppercase tracking-wider">Titular</span>
                              <span className="font-black text-foreground uppercase">{configuracionAdmin.nombre_titular}</span>
                            </div>

                            <div className="pt-3 mt-3 border-t border-blue-200/30 dark:border-blue-500/10">
                              <div className="flex justify-between items-end">
                                <span className="text-[10px] font-black uppercase text-blue-600/60 dark:text-blue-400/60">Total a Transferir</span>
                                <span className="text-2xl font-black text-foreground">
                                  {paqueteData
                                    ? formatCurrency(paqueteData.cantidad * sorteo.precio_boleto * (1 - (paqueteData.descuento / 100)))
                                    : formatCurrency(sorteo.precio_boleto * cantidad)}
                                </span>
                              </div>
                            </div>

                            <AppButton
                              variant="ghost"
                              size="sm"
                              className="w-full mt-4 bg-white dark:bg-white/5 border-blue-200 dark:border-blue-500/20 text-blue-600 font-black uppercase text-[9px] tracking-[0.2em] h-10 rounded-xl"
                              onClick={copyDatosBancarios}
                            >
                              Copiar todos los datos
                            </AppButton>
                          </div>
                        ) : (
                          <div className="py-8 flex flex-col items-center gap-3">
                            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Sincronizando banco...</span>
                          </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <div className="space-y-1">
                          <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-2">Referencia / Comprobante</Label>
                          <Input
                            value={numeroComprobante}
                            onChange={(e) => setNumeroComprobante(e.target.value)}
                            placeholder="Nº de transferencia"
                            className="h-12 bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 rounded-xl font-bold px-4 focus:ring-orange-500"
                          />
                        </div>
                        <AppButton
                          className="w-full h-14 bg-orange-600 hover:bg-orange-500 shadow-xl shadow-orange-600/20 rounded-2xl font-black uppercase tracking-wider text-sm"
                          onClick={handleComprarTransferencia}
                          loading={comprando}
                          icon={CheckCircle}
                        >
                          Confirmar Mi Compra
                        </AppButton>
                      </div>
                    </div>
                  ) : metodoPago === 'tarjeta' ? (
                    <div className="space-y-6">
                      <div className="p-6 bg-orange-500/5 border border-orange-500/10 rounded-3xl space-y-4">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-sm">
                            <span className="font-bold text-muted-foreground uppercase tracking-widest text-[10px]">Subtotal Boletos</span>
                            <span className="font-bold">
                              {paqueteData
                                ? formatCurrency(paqueteData.cantidad * sorteo.precio_boleto * (1 - (paqueteData.descuento / 100)))
                                : formatCurrency(sorteo.precio_boleto * cantidad)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-sm text-orange-600 dark:text-orange-400">
                            <span className="font-bold uppercase tracking-widest text-[10px]">Recargo Tarjeta ({payphoneSettings?.porcentaje_adicional}%)</span>
                            <span className="font-black">
                              +{formatCurrency(
                                (paqueteData
                                  ? (paqueteData.cantidad * sorteo.precio_boleto * (1 - (paqueteData.descuento / 100)))
                                  : (sorteo.precio_boleto * cantidad)) * (payphoneSettings?.porcentaje_adicional / 100)
                              )}
                            </span>
                          </div>
                          <div className="pt-4 mt-2 border-t border-orange-500/10">
                            <div className="flex justify-between items-end">
                              <span className="text-xs font-black uppercase tracking-[0.2em] text-orange-600">Total Final</span>
                              <span className="text-3xl font-black text-foreground">
                                {formatCurrency(
                                  (paqueteData
                                    ? (paqueteData.cantidad * sorteo.precio_boleto * (1 - (paqueteData.descuento / 100)))
                                    : (sorteo.precio_boleto * cantidad)) * (1 + (payphoneSettings?.porcentaje_adicional / 100))
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-4 p-5 bg-slate-50 dark:bg-white/5 rounded-3xl border border-slate-100 dark:border-white/10">
                        <ShieldCheck className="w-6 h-6 text-green-500 shrink-0" />
                        <div className="space-y-1">
                          <p className="text-[10px] font-black uppercase tracking-wider">Pago 100% Seguro</p>
                          <p className="text-[9px] text-muted-foreground font-medium leading-relaxed">
                            Tus datos están protegidos por PayPhone. No almacenamos información de tu tarjeta en nuestros servidores.
                          </p>
                        </div>
                      </div>

                      <AppButton
                        className="w-full h-14 bg-orange-600 hover:bg-orange-500 shadow-xl shadow-orange-600/20 rounded-2xl font-black uppercase tracking-wider text-sm"
                        onClick={handleProceedToPayPhone}
                        loading={comprando}
                        icon={CreditCard}
                      >
                        Pagar con Tarjeta
                      </AppButton>
                    </div>
                  ) : metodoPago === 'paypal' ? (
                    <div className="space-y-6">
                      <div className="p-6 bg-[#0070ba]/5 border border-[#0070ba]/10 rounded-3xl space-y-4">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-sm">
                            <span className="font-bold text-muted-foreground uppercase tracking-widest text-[10px]">Subtotal Boletos</span>
                            <span className="font-bold">
                              {paqueteData
                                ? formatCurrency(paqueteData.cantidad * sorteo.precio_boleto * (1 - (paqueteData.descuento / 100)))
                                : formatCurrency(sorteo.precio_boleto * cantidad)}
                            </span>
                          </div>
                          {paypalSettings?.porcentaje_adicional > 0 && (
                            <div className="flex justify-between items-center text-sm text-[#0070ba]">
                              <span className="font-bold uppercase tracking-widest text-[10px]">Recargo PayPal ({paypalSettings.porcentaje_adicional}%)</span>
                              <span className="font-black">
                                +{formatCurrency(
                                  (paqueteData
                                    ? (paqueteData.cantidad * sorteo.precio_boleto * (1 - (paqueteData.descuento / 100)))
                                    : (sorteo.precio_boleto * cantidad)) * (paypalSettings.porcentaje_adicional / 100)
                                )}
                              </span>
                            </div>
                          )}
                          <div className="pt-4 mt-2 border-t border-[#0070ba]/10">
                            <div className="flex justify-between items-end">
                              <span className="text-xs font-black uppercase tracking-[0.2em] text-[#0070ba]">Total Final</span>
                              <span className="text-3xl font-black text-foreground">
                                {formatCurrency(
                                  (paqueteData
                                    ? (paqueteData.cantidad * sorteo.precio_boleto * (1 - (paqueteData.descuento / 100)))
                                    : (sorteo.precio_boleto * cantidad)) * (1 + ((paypalSettings?.porcentaje_adicional || 0) / 100))
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-4 p-5 bg-slate-50 dark:bg-white/5 rounded-3xl border border-slate-100 dark:border-white/10">
                        <ShieldCheck className="w-6 h-6 text-[#0070ba] shrink-0" />
                        <div className="space-y-1">
                          <p className="text-[10px] font-black uppercase tracking-wider">Pago Seguro con PayPal</p>
                          <p className="text-[9px] text-muted-foreground font-medium leading-relaxed">
                            Serás redirigido a PayPal para completar tu pago de forma segura. No compartimos tus datos financieros.
                          </p>
                        </div>
                      </div>

                      <AppButton
                        className="w-full h-14 bg-[#0070ba] hover:bg-[#003087] shadow-xl shadow-[#0070ba]/20 rounded-2xl font-black uppercase tracking-wider text-sm text-white"
                        onClick={handleProceedToPayPal}
                        loading={comprando}
                        icon={DollarSign}
                      >
                        Pagar con PayPal
                      </AppButton>
                    </div>
                  ) : null}
                </div>
              ) : showPaypalButtons && paypalSettings?.client_id ? (
                <div className="py-6 space-y-6 animate-in zoom-in duration-500">
                  <div className="text-center space-y-2">
                    <div className="inline-flex items-center justify-center p-3 bg-[#0070ba]/10 rounded-2xl mb-2">
                      <DollarSign className="w-8 h-8 text-[#0070ba]" />
                    </div>
                    <h3 className="text-xl font-black uppercase italic tracking-tighter">PayPal <span className="text-[#0070ba]">Checkout</span></h3>
                  </div>

                  <div className="min-h-[120px] bg-slate-50 dark:bg-white/5 rounded-3xl p-6 border border-slate-100 dark:border-white/5">
                    <PayPalScriptProvider options={{
                      clientId: paypalSettings.client_id,
                      currency: "USD",
                      intent: "capture"
                    }}>
                      <PayPalButtons
                        style={{ layout: "vertical", color: "blue", shape: "rect", label: "pay", height: 50 }}
                        createOrder={handlePayPalCreateOrder}
                        onApprove={handlePayPalApprove}
                        onCancel={() => {
                          toast.info('Pago cancelado');
                          setShowPaypalButtons(false);
                          setComprando(false);
                        }}
                        onError={(err) => {
                          console.error('Error PayPal:', err);
                          toast.error('Error en la pasarela de PayPal');
                          setComprando(false);
                        }}
                      />
                    </PayPalScriptProvider>
                  </div>

                  <AppButton
                    variant="ghost"
                    className="w-full h-12 rounded-xl text-muted-foreground hover:text-foreground font-bold uppercase text-[10px] tracking-widest"
                    onClick={() => {
                      setShowPaypalButtons(false);
                      setPaypalPurchaseData(null);
                      setComprando(false);
                    }}
                  >
                    Cancelar y volver
                  </AppButton>
                </div>
              ) : (
                <div className="pt-2 pb-32 sm:pb-4 space-y-4 animate-in zoom-in duration-500">
                  <div className="text-center space-y-2">
                    <div className="inline-flex items-center justify-center p-3 bg-orange-500/10 rounded-2xl mb-2">
                      <CreditCard className="w-8 h-8 text-orange-500" />
                    </div>
                    <h3 className="text-xl font-black uppercase italic tracking-tighter">Pasarela <span className="text-orange-500">Activa</span></h3>
                  </div>

                  <div id="pp-button" className="flex justify-center w-full min-h-[400px]">
                    <div className="flex flex-col items-center justify-center gap-3 mt-12">
                      <div className="w-8 h-8 border-3 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
                      <span className="text-[10px] font-black uppercase text-orange-500 animate-pulse tracking-widest">Iniciando Checkout Seguro...</span>
                    </div>
                  </div>

                  <AppButton
                    variant="ghost"
                    className="w-full h-12 rounded-xl text-muted-foreground hover:text-foreground font-bold uppercase text-[10px] tracking-widest"
                    onClick={() => {
                      setShowPayphoneButton(false);
                      setComprando(false);
                    }}
                  >
                    Cancelar y volver
                  </AppButton>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FastCheckoutModal;
